import { ApiClientError } from './client';
import type { TimesheetResponse } from './client';
import type { ImportResponse } from '../utils/timesheetCsv';

/**
 * POST /api/timesheet/save — Sprint 5 write path.
 *
 * camelCase outbound (locked by sprint5.md cross-agent contract).
 * Response 200 = the same TimesheetResponse shape as GET /api/timesheet/current
 * (refreshed post-write).
 *
 * Error codes (per cross-agent contract):
 *   400 bad_request          — shape errors / hours <0 / dayIndex out of [1,15]
 *   401 vp_unauthorized      — VP cache evicted; redirect to /login
 *   409 stale_master         — concurrency conflict; offer refresh-and-retry
 *   422 vp_rejected          — VP refused on validation grounds (comment/phase
 *                              required, …); detail carries the per-row reasons
 *   502 vp_write_failed      — generic VP write failure; draft preserved
 */
export interface SaveTimesheetEntry {
  wbs1: string;
  wbs2?: string;
  wbs3?: string;
  /** 1..15 (per cross-agent contract). */
  dayIndex: number;
  hours: number;
  comment?: string;
  laborCode?: string;
  signatureHashes?: string[];
  /** Source events behind this entry — recorded so triage can say "already published". */
  eventIds?: string[];
  /** VP time-category code (ADR-0044); VP resolves the alias onto its WBS at save. */
  category?: string;
}

export interface SaveTimesheetRequest {
  /** ISO YYYY-MM-DD; backend re-derives but field is required. */
  periodEnd: string;
  entries: SaveTimesheetEntry[];
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    // The API returns RFC 7807 ProblemDetails: `title` is the machine code the
    // FE switches on, `detail` is the human message. (Reading `error`/`message`
    // here silently dropped VP's rejection reason and surfaced a bare status.)
    let body: { title?: string; detail?: string } | undefined;
    try {
      body = (await res.json()) as { title?: string; detail?: string };
    } catch {
      // not JSON
    }
    const message = body?.detail ?? body?.title ?? `${res.status} ${res.statusText}`;
    const code = body?.title ?? `http_${res.status}`;
    throw new ApiClientError(message, code, res.status);
  }

  return (await res.json()) as T;
}

export function saveTimesheet(payload: SaveTimesheetRequest): Promise<TimesheetResponse> {
  return postJson<TimesheetResponse>('/api/timesheet/save', payload);
}

// POST /api/timesheet/import — the server checks the file against the caller's
// VP session and returns the rows it accepts. Writes nothing.
export function importTimesheetCsv(periodEnd: string, csv: string): Promise<ImportResponse> {
  return postJson<ImportResponse>('/api/timesheet/import', { periodEnd, csv });
}
